import { AsyncPipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  AssetColorLabel,
  AssetDto,
  AssetStatus,
  GalleryColorLabel,
  GalleryDto,
  GalleryFeedbackOptions,
} from '@nusign/picflow-backend-types';
import { Observable, of, shareReplay, Subject, switchMap, takeUntil } from 'rxjs';
import { billingScopes } from '../../../billing-consts';
import { AssetStatusButtonComponent } from '../../../gallery/components/asset-status-button/asset-status-button.component';
import { ColorLabelContextComponent } from '../../../gallery/components/color-label-context/color-label-context.component';
import { FavoriteButtonComponent } from '../../../gallery/components/favorite-button/favorite-button.component';
import { GallerySettingsMenuComponent } from '../../../gallery/menus/gallery-settings-menu/gallery-settings-menu.component';
import { isPicflowIOSApp } from '../../../lib/is-ios-app';
import { AssetApprovalLimitService } from '../../../services/asset-approval-limit.service';
import { AssetService } from '../../../services/asset.service';
import { DialogService } from '../../../services/dialog.service';
import { PermissionService } from '../../../services/permission.service';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SmoothCounterComponent } from '../../../shared/components/smooth-counter/smooth-counter.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { AssetVersionMenuComponent } from '../asset-version-menu/asset-version-menu.component';

@Component({
  selector: 'app-asset-page-footer',
  templateUrl: './asset-page-footer.component.html',
  styleUrls: ['./asset-page-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ButtonDirective,
    TooltipDirective,
    SvgComponent,
    ContextMenuComponent,
    SmoothCounterComponent,
    AssetStatusButtonComponent,
    ColorLabelContextComponent,
    AssetVersionMenuComponent,
    UpperCasePipe,
    FavoriteButtonComponent,
  ],
})
export class AssetPageFooterComponent implements OnChanges, OnDestroy, OnInit {
  private dialog = inject(DialogService);
  private assetService = inject(AssetService);
  private assetApprovalLimitService = inject(AssetApprovalLimitService);
  private check = inject(ChangeDetectorRef);
  private permissionService = inject(PermissionService);
  readonly asset = input<AssetDto>(undefined);
  readonly gallery = input<GalleryDto>(undefined);
  @Input() sideMenuOpen: boolean;
  @Input() clientMode: boolean;
  @Input() currentLabel: GalleryColorLabel;
  @Input() colorLabels: GalleryColorLabel[] = [];
  readonly galleryPermissions = input.required<GalleryDto['permissions']>();
  @Input() commentPlacementMode: boolean;

  @Output() onToggleLike = new EventEmitter();
  @Output() onUpdateAssetStatus = new EventEmitter();
  @Output() onUpdateColorLabel = new EventEmitter();
  @Output() toggleCommentPlacementMode = new EventEmitter();
  @Output() onStartAnnotation = new EventEmitter();

  isPicflowIOSApp = isPicflowIOSApp();

  private showApprovedBadgeOnly = computed(() => {
    const asset = this.asset();
    const gallery = this.gallery();
    if (!asset || !gallery) return;
    if (asset.status !== 'approved') return false;
    if (!gallery.download_single_asset) return false;
    return true;
  });

  display = computed(() => {
    const allowedAndEnabled = this.allowedAndEnabledFeedbackOptions();
    if (!allowedAndEnabled) return;
    if (!allowedAndEnabled.includes('status') && this.showApprovedBadgeOnly()) {
      allowedAndEnabled.push('display-status');
    }
    return allowedAndEnabled;
  });

  allowedAndEnabledFeedbackOptions = computed(() => {
    return this.permissionService.getAllowedAndEnabledFeedbackOptions(
      this.gallery(),
      this.galleryPermissions()?.can_review_if_authenticated
    ) as (GalleryFeedbackOptions | 'display-status')[];
  });
  approvedAssetCount$ = toObservable(this.gallery).pipe(
    switchMap(gallery => this.assetApprovalLimitService.getApprovedAssetCountForGallery(gallery.id)),
    shareReplay(1)
  );
  destroyed$ = new Subject<void>();
  allowVersionHistory = computed(() => {
    const gallery = this.gallery();
    const galleryPermissions = this.galleryPermissions();
    if (!gallery) return;
    if (!galleryPermissions) return;
    if (!this.permissionService.canDisplayElement(billingScopes.assetVersion.multiple)) return false;
    if (galleryPermissions.can_edit) return true;
    return gallery.version_history_enabled;
  });

  favoritedUsersString$: Observable<string> = of('Loading...');

  ngOnInit(): void {
    this.assetService.updates.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.check.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.asset) {
      this.favoritedUsersString$ = of('Loading...');
    }
  }

  toggleLike() {
    this.onToggleLike.emit();
  }

  async updateAssetStatus(status: AssetStatus) {
    this.onUpdateAssetStatus.emit(status);
  }

  updateColorLabel(label: AssetColorLabel) {
    this.onUpdateColorLabel.emit(label);
  }

  openReviewSettings() {
    this.dialog.open(GallerySettingsMenuComponent, { gallery: this.gallery() });
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
